import {getLFCData} from '_logics/LFCUtil';
import {CancelToken} from 'axios';
import {EnqueueSnackbar} from 'notistack';
import {TFunction} from 'i18next';
import {RECEIVE_MAIL_TYPE} from '../constants/alertSettingsConfig';

// 型: 受信メール種別 (1 | 2)
export type ReceiveMailType = (typeof RECEIVE_MAIL_TYPE)[keyof typeof RECEIVE_MAIL_TYPE];

/**
 * 通知先管理API
 * アラート設定APIから通知先管理を分離
 */
export class NotificationApi {
  /**
   * 通知先一覧を取得
   */
  static async getNotifications(
    alertId: string,
    snack: EnqueueSnackbar,
    cancelToken: CancelToken,
    t: TFunction
  ) {
    return await getLFCData({
      snack,
      sql_id: 136,
      parameters: {alertId},
      name: '',
      cancelToken,
      t
    });
  }

  /**
   * 通知先を追加
   */
  static async addNotification(
    alertId: string,
    recipients: string,
    receiveMailType: ReceiveMailType,
    snack: EnqueueSnackbar,
    cancelToken: CancelToken,
    t: TFunction
  ) {
    return await getLFCData({
      snack,
      sql_id: 315,
      parameters: {
        alert_id: alertId,
        recipients,
        receive_mail: receiveMailType
      },
      name: '',
      cancelToken,
      t
    });
  }

  /**
   * 通知先を削除
   */
  static async deleteNotification(
    alertId: string,
    recipients: string,
    snack: EnqueueSnackbar,
    cancelToken: CancelToken,
    t: TFunction
  ) {
    return await getLFCData({
      snack,
      sql_id: 316,
      parameters: {
        alert_id: alertId,
        recipients
      },
      name: '',
      cancelToken,
      t
    });
  }

  /**
   * 通知先を一括追加（新規アラート作成時専用）
   */
  static async bulkAddNotifications(
    alertId: string,
    notifications: Array<{email: string; receiveMailType: ReceiveMailType}>,
    emails: Array<{id: string; email: string}>,
    snack: EnqueueSnackbar,
    cancelToken: CancelToken,
    t: TFunction
  ) {
    // 並列実行で高速化
    return await Promise.all(
      notifications.map(notification => {
        // メールアドレスから対応するユーザー名（id）を検索
        const emailData = emails.find(e => e.email === notification.email);
        const username = emailData?.id || notification.email; // フォールバック

        return this.addNotification(
          alertId,
          username,
          notification.receiveMailType,
          snack,
          cancelToken,
          t
        );
      })
    );
  }

  /**
   * 通知先の差分を適用（編集時専用）
   */
  static async applyNotificationChanges(
    alertId: string,
    existingRecipients: {recipients: string; receive_mail: ReceiveMailType}[],
    currentNotifications: Array<{email: string; receiveMailType: ReceiveMailType}>,
    emails: Array<{id: string; email: string}>,
    snack: EnqueueSnackbar,
    cancelToken: CancelToken,
    t: TFunction
  ) {
    // メールアドレスからユーザー名を取得するヘルパー関数
    const getUsername = (email: string): string => {
      const emailData = emails.find(e => e.email === email);
      return emailData?.id || email; // フォールバック
    };

    // 削除対象
    const toDelete = existingRecipients.filter(
      item => !currentNotifications.some(n => getUsername(n.email) === item.recipients)
    );

    // 更新対象
    const toUpdate = currentNotifications.filter(item =>
      existingRecipients.some(
        n => getUsername(item.email) === n.recipients && n.receive_mail !== item.receiveMailType
      )
    );

    // 追加対象
    const toAdd = currentNotifications.filter(
      item => !existingRecipients.some(n => getUsername(item.email) === n.recipients)
    );

    // 削除実行
    await Promise.all(
      toDelete.map(item => this.deleteNotification(alertId, item.recipients, snack, cancelToken, t))
    );

    // 更新実行（削除して再追加）と追加実行
    await Promise.all(
      [...toUpdate, ...toAdd].map(item => {
        return this.addNotification(
          alertId,
          getUsername(item.email),
          item.receiveMailType,
          snack,
          cancelToken,
          t
        );
      })
    );
  }
}
